import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type {
  PrepareNativeLabOrderSetRequestT,
  PrepareNativeLabOrderSetResponseT,
  SubmitNativeLabOrderSetRequestT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { closeCopilotOverlay } from '@/shared/stores/ui';
import {
  QUEST_CLINICAL_NOTES_MAX_LENGTH,
  type ICD10CodeT,
  usePrepareNativeLabOrderSetQuery,
} from '@/entities/labs';
import { extractApiErrorDetail } from '@/shared';
import { analytics, ANALYTICS_EVENTS, isRevisionFlow } from '@/shared/lib';

import type { NativeLabOrderWizardStepId, NativeOrderSetLabOrderDraft } from './types.js';
import { INITIAL_MARKER_ADD_ON_DRAFT } from './types.js';
import type { NativeLabOrderSelectionItem } from './nativeLabOrderSelection.js';
import { useNativeLabOrderSubmission } from './useNativeLabOrderSubmission.js';
import { useLabOrderFinalization } from './useLabOrderFinalization.js';
import type { NativeOrderSetLabOrderWizardPrefill } from './labOrderWizardPrefill.js';
import { toAoeAnswersArray, areRequiredQuestionsAnswered } from './aoeHelpers.js';

function buildNativeOrderData(
  draft: NativeOrderSetLabOrderDraft,
  preparation: PrepareNativeLabOrderSetResponseT
): Omit<SubmitNativeLabOrderSetRequestT, 'orderAttemptId'> {
  return {
    labSlug: draft.labSlug,
    collectionMethod: draft.method,
    labTestIds: preparation.labTestIds,
    markerIds: preparation.markerIds,
    aoeAnswers: toAoeAnswersArray(draft.aoeAnswersById, preparation.aoeSchema),
    notes: draft.notes.trim() || undefined,
    priority: draft.priority || undefined,
    icdCodes: draft.icdCodes.map(({ code }) => code),
  };
}

const manualAttemptKeysByUserId = new Map<string, string[]>();

function getManualAttemptKey(userId: string): string {
  return manualAttemptKeysByUserId.get(userId)?.at(-1) ?? `manual:${userId}`;
}

function createSeparateManualAttemptKey(userId: string): string {
  return `manual:${userId}:${crypto.randomUUID()}`;
}

function rememberManualAttempt(userId: string, attemptKey: string): void {
  const currentKeys = manualAttemptKeysByUserId.get(userId) ?? [getManualAttemptKey(userId)];
  if (currentKeys.at(-1) === attemptKey) return;
  manualAttemptKeysByUserId.set(userId, [...currentKeys, attemptKey]);
}

function finishManualAttempt(userId: string, attemptKey: string): void {
  const currentKeys = manualAttemptKeysByUserId.get(userId);
  if (!currentKeys) return;
  const remainingKeys = currentKeys.filter((key) => key !== attemptKey);
  if (remainingKeys.length <= 1) {
    manualAttemptKeysByUserId.delete(userId);
  } else {
    manualAttemptKeysByUserId.set(userId, remainingKeys);
  }
}

function createInitialDraft(
  prefill: NativeOrderSetLabOrderWizardPrefill | null
): NativeOrderSetLabOrderDraft {
  if (prefill === null) return INITIAL_MARKER_ADD_ON_DRAFT;
  return {
    labSlug: prefill.labSlug,
    method: prefill.collectionMethod,
    labTestIds: prefill.labTestIds,
    markerIds: prefill.markerIds,
    icdCodes: prefill.icdCodes ?? [],
    notes: prefill.notes ?? '',
    priority: prefill.priority ?? false,
    aoeAnswersById: {},
  };
}

function createRecoveredNativeDraft(
  data: Omit<SubmitNativeLabOrderSetRequestT, 'orderAttemptId'>
): NativeOrderSetLabOrderDraft {
  return {
    labSlug: data.labSlug,
    method: data.collectionMethod,
    labTestIds: data.labTestIds ?? [],
    markerIds: data.markerIds ?? [],
    icdCodes: data.icdCodes.map((code) => ({ code, description: '' })),
    notes: data.notes ?? '',
    priority: data.priority ?? false,
    aoeAnswersById: Object.fromEntries(
      (data.aoeAnswers ?? []).map(({ question_id, answer_code }) => [question_id, answer_code])
    ),
  };
}

function getInitialStep(
  prefill: NativeOrderSetLabOrderWizardPrefill | null,
  hasRecoveredSubmission: boolean
): NativeLabOrderWizardStepId {
  if (hasRecoveredSubmission) return 'review';
  if (prefill === null) return 'select-test';
  return isRevisionFlow(prefill.sourceProposedAction) ? 'select-test' : 'disease-codes';
}

const STEP_META: Record<NativeLabOrderWizardStepId, { title: string; subtitle: string }> = {
  'select-test': {
    title: 'Choose tests',
    subtitle:
      'Build one Quest order for this patient. Panels and individual tests can be combined.',
  },
  'disease-codes': {
    title: 'Add diagnoses for this order',
    subtitle: 'These ICD-10 codes will be included on the shared Quest requisition.',
  },
  review: {
    title: 'Review & submit',
    subtitle: 'Confirm the details below, then submit your order.',
  },
};

function getPreparationValidationError(
  isPending: boolean,
  preparation: PrepareNativeLabOrderSetResponseT | undefined,
  preparationError: string | null
): string | null {
  if (isPending) return 'The lab order set is still being validated by Junction.';
  if (!preparation) return preparationError ?? 'Could not validate this lab order set.';
  return null;
}

function validateForReview({
  draft,
  preparation,
  preparationError,
  isPreparing,
}: {
  draft: NativeOrderSetLabOrderDraft;
  preparation: PrepareNativeLabOrderSetResponseT | undefined;
  preparationError: string | null;
  isPreparing: boolean;
}): { kind: 'preparation' | 'diagnosis' | 'notes' | 'aoe'; message: string } | null {
  const preparationValidationError = getPreparationValidationError(
    isPreparing,
    preparation,
    preparationError
  );
  if (preparationValidationError) {
    return { kind: 'preparation', message: preparationValidationError };
  }
  if (draft.icdCodes.length === 0) {
    return { kind: 'diagnosis', message: 'Please add at least one ICD-10 diagnosis code.' };
  }
  if (draft.notes.length > QUEST_CLINICAL_NOTES_MAX_LENGTH) {
    return {
      kind: 'notes',
      message: `Clinical notes must be ${QUEST_CLINICAL_NOTES_MAX_LENGTH} characters or fewer.`,
    };
  }
  if (
    preparation?.aoeSchema &&
    !areRequiredQuestionsAnswered(preparation.aoeSchema, draft.aoeAnswersById)
  ) {
    return { kind: 'aoe', message: 'Please answer all required questions before continuing.' };
  }
  return null;
}

export function useNativeLabOrderWizard({
  userId,
  prefill,
}: {
  userId: string;
  prefill: NativeOrderSetLabOrderWizardPrefill | null;
}) {
  const finalization = useLabOrderFinalization({
    userId,
    sourceProposedAction: prefill?.sourceProposedAction,
  });
  const sourceRecoveryKey = finalization.recoveryKey;
  const [manualAttemptKey, setManualAttemptKey] = useState<string | undefined>(() =>
    !sourceRecoveryKey ? getManualAttemptKey(userId) : undefined
  );
  const nativeAttemptKey = sourceRecoveryKey
    ? `proposed-action:${sourceRecoveryKey}`
    : manualAttemptKey;
  const nativeSubmission = useNativeLabOrderSubmission(userId, nativeAttemptKey);
  const recoveredSubmittedOrder = finalization.submittedOrder;
  const recoveredNativeData = nativeSubmission.retryData;

  const [stepId, setStepId] = useState<NativeLabOrderWizardStepId>(() =>
    getInitialStep(prefill, recoveredSubmittedOrder !== null || recoveredNativeData !== undefined)
  );
  const [draft, setDraft] = useState<NativeOrderSetLabOrderDraft>(() =>
    recoveredNativeData
      ? createRecoveredNativeDraft(recoveredNativeData)
      : createInitialDraft(prefill)
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [locallySelectedItems, setLocallySelectedItems] = useState<NativeLabOrderSelectionItem[]>(
    []
  );

  const preparationRequest = useMemo<PrepareNativeLabOrderSetRequestT | undefined>(() => {
    if (draft.labTestIds.length === 0 && draft.markerIds.length === 0) {
      return undefined;
    }
    return {
      labSlug: draft.labSlug,
      collectionMethod: draft.method,
      labTestIds: draft.labTestIds,
      markerIds: draft.markerIds,
    };
  }, [draft]);
  const preparationQuery = usePrepareNativeLabOrderSetQuery(preparationRequest);
  const preparation =
    preparationRequest && preparationQuery.data?.type === 'success'
      ? preparationQuery.data.preparation
      : undefined;
  const preparationError =
    preparationQuery.data?.type === 'failure'
      ? preparationQuery.data.details
      : preparationQuery.isError
        ? (extractApiErrorDetail(preparationQuery.error) ??
          'Could not validate this lab order set.')
        : null;
  const displayedPreparationError =
    finalization.submittedOrder || nativeSubmission.retryData ? null : preparationError;
  const effectiveAoeSchema = preparation?.aoeSchema;
  const isResolvingAoe = preparationRequest !== undefined && preparationQuery.isPending;

  const selectedItems = useMemo<NativeLabOrderSelectionItem[]>(() => {
    if (preparation) {
      return [
        ...preparation.labTests.map((item) => ({ ...item, source: 'lab_test' as const })),
        ...preparation.markers.map((item) => ({ ...item, source: 'marker' as const })),
      ];
    }

    const localMarkersById = new Map<string, NativeLabOrderSelectionItem>(
      locallySelectedItems.map((item) => [item.id, item])
    );
    return [
      ...draft.labTestIds.map((id) => ({
        source: 'lab_test' as const,
        id,
        name: 'Loading selected panel…',
        kind: 'configured_test' as const,
        components: [],
      })),
      ...draft.markerIds.map(
        (id): NativeLabOrderSelectionItem =>
          localMarkersById.get(id) ?? {
            source: 'marker',
            id,
            name: 'Loading selected test…',
            kind: 'unknown',
            components: [],
          }
      ),
    ];
  }, [draft, locallySelectedItems, preparation]);

  const submissionState = nativeSubmission.state;
  const isDraftLocked = finalization.submittedOrder !== null || nativeSubmission.isDraftLocked;
  const isSubmitting = nativeSubmission.isSubmitting;
  const [isEditableMode, setIsEditableMode] = useState(
    !recoveredSubmittedOrder &&
      !recoveredNativeData &&
      (!prefill?.sourceProposedAction || isRevisionFlow(prefill.sourceProposedAction))
  );

  const analyticsContext = useMemo(
    () => ({ actor: 'clinician' as const, patientUserId: userId }),
    [userId]
  );

  const wizardOpenedRef = useRef(false);
  useEffect(() => {
    if (wizardOpenedRef.current) return;
    wizardOpenedRef.current = true;
    void analytics.track({
      name: ANALYTICS_EVENTS.labs.wizardOpened,
      context: analyticsContext,
    });
  }, [analyticsContext]);

  const prevStepRef = useRef(stepId);
  useEffect(() => {
    if (prevStepRef.current === stepId) return;
    prevStepRef.current = stepId;
    void analytics.track({
      name: ANALYTICS_EVENTS.labs.wizardStepViewed,
      context: analyticsContext,
      properties: { stepId },
    });
  }, [stepId, analyticsContext]);

  const stepMeta = STEP_META[stepId];

  const handleAddItem = (item: NativeLabOrderSelectionItem & { source: 'marker' }) => {
    if (draft.markerIds.includes(item.id)) return;
    setLocallySelectedItems((current) => [...current, item]);
    setDraft((current) => ({
      ...current,
      markerIds: [...current.markerIds, item.id],
    }));
    setFormError(null);
  };

  const handleRemoveItem = (item: NativeLabOrderSelectionItem) => {
    setLocallySelectedItems((current) =>
      current.filter((selected) => item.source !== 'marker' || selected.id !== item.id)
    );
    setDraft((current) => {
      return item.source === 'lab_test'
        ? { ...current, labTestIds: current.labTestIds.filter((id) => id !== item.id) }
        : { ...current, markerIds: current.markerIds.filter((id) => id !== item.id) };
    });
    setFormError(null);
  };

  const updateDraft = (
    updates: Partial<Pick<NativeOrderSetLabOrderDraft, 'notes' | 'priority' | 'aoeAnswersById'>>
  ) => {
    setDraft((currentDraft) => ({ ...currentDraft, ...updates }));
  };

  const handleAddIcdCode = useCallback((code: ICD10CodeT) => {
    setDraft((d) => {
      if (d.icdCodes.some((c) => c.code === code.code)) return d;
      return { ...d, icdCodes: [...d.icdCodes, code] };
    });
  }, []);

  const handleRemoveIcdCode = useCallback((code: string) => {
    setDraft((d) => ({
      ...d,
      icdCodes: d.icdCodes.filter((c) => c.code !== code),
    }));
  }, []);

  const validateSelectionAndProceed = () => {
    setFormError(null);
    if (draft.labTestIds.length === 0 && draft.markerIds.length === 0) {
      setFormError('Please add at least one test.');
      return;
    }
    const validationError = getPreparationValidationError(
      preparationQuery.isPending,
      preparation,
      preparationError
    );
    if (validationError) {
      setFormError(validationError);
      return;
    }
    if (!preparation) return;

    setDraft({
      ...draft,
      labTestIds: preparation.labTestIds,
      markerIds: preparation.markerIds,
    });
    setStepId('disease-codes');
  };

  const validateAndProceedToReview = () => {
    setFormError(null);
    const validationError = validateForReview({
      draft,
      preparation,
      preparationError,
      isPreparing: preparationQuery.isPending,
    });
    if (validationError) {
      setFormError(validationError.message);
      return;
    }

    setStepId('review');
  };

  const handleBack = () => {
    setFormError(null);
    if (stepId === 'review') setStepId('disease-codes');
    if (stepId === 'disease-codes') setStepId('select-test');
  };

  const handleSubmit = async () => {
    setFormError(null);
    const retryData = nativeSubmission.retryData;

    const validationError = retryData
      ? null
      : validateForReview({
          draft,
          preparation,
          preparationError,
          isPreparing: preparationQuery.isPending,
        });
    if (validationError) {
      setIsEditableMode(true);
      setFormError(
        validationError.kind === 'aoe'
          ? 'This order requires answers to additional questions before it can be submitted.'
          : validationError.message
      );
      setStepId('disease-codes');
      return;
    }

    void analytics.track({
      name: ANALYTICS_EVENTS.labs.orderSubmitAttempted,
      context: analyticsContext,
      properties: {
        mode: 'native_order_set',
        hasNotes: draft.notes.trim().length > 0,
        notesLength: draft.notes.trim().length > 0 ? draft.notes.trim().length : undefined,
        aoeAnswerCount: Object.keys(draft.aoeAnswersById).length,
        prioritySet: draft.priority,
      },
    });

    const data = retryData ?? (preparation ? buildNativeOrderData(draft, preparation) : null);
    if (!data) return;
    if (!sourceRecoveryKey && manualAttemptKey) {
      rememberManualAttempt(userId, manualAttemptKey);
    }
    const submission = await nativeSubmission.submit({ data });
    const result = submission.result;
    if (!sourceRecoveryKey && manualAttemptKey && !submission.requiresRecovery) {
      finishManualAttempt(userId, manualAttemptKey);
    }

    const submitOutcome = result ? 'success' : 'failure';
    void analytics.track({
      name: ANALYTICS_EVENTS.labs.orderSubmitResult,
      context: analyticsContext,
      properties: { mode: 'native_order_set', outcome: submitOutcome },
    });

    if (result) {
      if (await finalization.finalize(result)) {
        closeCopilotOverlay('labOrderWizard');
      }
    }
  };

  const handleRetryFinalization = async () => {
    setFormError(null);
    if (await finalization.retry()) {
      closeCopilotOverlay('labOrderWizard');
    }
  };

  const handleStartSeparateManualOrder = () => {
    setManualAttemptKey(createSeparateManualAttemptKey(userId));
    setDraft(createInitialDraft(null));
    setLocallySelectedItems([]);
    setFormError(null);
    setIsEditableMode(true);
    setStepId('select-test');
  };

  const backHidden = stepId === 'select-test' || (stepId === 'disease-codes' && !isEditableMode);
  const backDisabled = isDraftLocked || (!isEditableMode && stepId === 'review');

  const submitLabel = isSubmitting
    ? 'Submitting…'
    : finalization.isFinalizing
      ? 'Updating action…'
      : finalization.submittedOrder
        ? 'Retry action update'
        : nativeSubmission.retryData
          ? 'Retry submission'
          : isDraftLocked
            ? 'Submitted'
            : 'Submit order';
  const canRetryNativeSubmission = nativeSubmission.retryData !== undefined;
  const canStartSeparateManualOrder =
    prefill === null &&
    submissionState.status === 'failure' &&
    nativeSubmission.retryData !== undefined;

  const nextDisabled =
    stepId !== 'review' &&
    (preparation === undefined || preparationQuery.isPending || preparationError !== null);

  const handlePrimarySubmit = finalization.submittedOrder ? handleRetryFinalization : handleSubmit;
  const handlePrimaryAction =
    stepId === 'select-test'
      ? validateSelectionAndProceed
      : stepId === 'disease-codes'
        ? validateAndProceedToReview
        : handlePrimarySubmit;
  const primaryDisabled =
    stepId === 'review'
      ? isSubmitting ||
        finalization.isFinalizing ||
        (!finalization.submittedOrder &&
          !canRetryNativeSubmission &&
          (isDraftLocked || isResolvingAoe))
      : nextDisabled;
  const handleEditTests =
    isEditableMode && !isDraftLocked ? () => setStepId('select-test') : undefined;
  const handleEditDetails =
    isEditableMode && !isDraftLocked ? () => setStepId('disease-codes') : undefined;

  return {
    stepId,
    stepMeta,
    draft,
    errorMessage: formError ?? finalization.errorMessage ?? displayedPreparationError,
    effectiveAoeSchema,
    isResolvingAoe,
    selectedItems,
    submissionState,
    isEditableMode,
    backHidden,
    backDisabled,
    canStartSeparateManualOrder,
    primaryLabel: stepId === 'review' ? submitLabel : 'Next',
    primaryDisabled,
    answeredAoeQuestionCount: toAoeAnswersArray(draft.aoeAnswersById, effectiveAoeSchema).length,
    handleAddItem,
    handleRemoveItem,
    handleAoeChange: (aoeAnswersById: Record<string, string>) => updateDraft({ aoeAnswersById }),
    handleNotesChange: (notes: string) => updateDraft({ notes }),
    handlePriorityChange: (priority: boolean) => updateDraft({ priority }),
    handleAddIcdCode,
    handleRemoveIcdCode,
    handleBack,
    handleStartSeparateManualOrder,
    handlePrimaryAction,
    handleEditTests,
    handleEditDetails,
  };
}
